/**
 * AVYU Store — breath-paced reveals.
 * Adds .is-visible to .reveal elements as they enter the viewport.
 * Honors prefers-reduced-motion (CSS also guards this).
 */
(function () {
  "use strict";

  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var items = document.querySelectorAll(".reveal");

  if (!("IntersectionObserver" in window) || reduced) {
    items.forEach(function (el) {
      el.classList.add("is-visible");
    });
  } else {
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -6% 0px" }
    );

    items.forEach(function (el) {
      observer.observe(el);
    });
  }

  // Header hairline once scrolled
  var header = document.querySelector("[data-header]");
  if (header) {
    var onScroll = function () {
      header.classList.toggle("is-scrolled", window.scrollY > 8);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }

  // Mobile nav
  var burger = document.querySelector("[data-burger]");
  var mobileNav = document.querySelector("[data-mobile-nav]");
  var closeNav = document.querySelector("[data-mobile-nav-close]");

  function focusableElements(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );
  }

  function renderMobileNav(open) {
    if (!mobileNav) return;
    mobileNav.classList.toggle("is-open", open);
    mobileNav.setAttribute("aria-hidden", String(!open));
    if (burger) burger.setAttribute("aria-expanded", String(open));
    if (open && closeNav) {
      window.setTimeout(function () { closeNav.focus(); }, 80);
    }
    if (!open && burger) burger.focus();
  }

  var overlays = window.AVYU.overlays;
  if (mobileNav) overlays.register("menu", renderMobileNav);
  function setMobileNav(open) {
    if (open) overlays.open("menu");
    else overlays.close("menu");
  }

  if (burger && mobileNav) {
    burger.addEventListener("click", function () { setMobileNav(true); });
  }
  if (closeNav) {
    closeNav.addEventListener("click", function () { setMobileNav(false); });
  }

  if (mobileNav) {
    mobileNav.addEventListener("click", function (event) {
      var link = event.target.closest("a[href]");
      if (link && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && link.target !== "_blank") {
        event.preventDefault();
        var destination = link.href;
        overlays.close("menu", function () { window.location.assign(destination); });
      }
    });
  }

  document.addEventListener("keydown", function (event) {
    if (!mobileNav || !mobileNav.classList.contains("is-open")) return;
    if (event.key === "Escape") { event.preventDefault(); setMobileNav(false); }
    if (event.key !== "Tab") return;
    var elements = focusableElements(mobileNav);
    var first = elements[0];
    var last = elements[elements.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  // Collapse footer link groups on phones while keeping every link available.
  var footer = document.querySelector("[data-footer]");
  if (footer) {
    var footerMedia = window.matchMedia("(max-width: 46rem)");
    var footerToggles = footer.querySelectorAll("[data-footer-toggle]");
    var syncFooter = function () {
      footer.classList.toggle("footer--accordion-ready", footerMedia.matches);
      footerToggles.forEach(function (button) {
        button.closest(".footer__column").classList.remove("is-open");
        button.setAttribute("aria-expanded", String(!footerMedia.matches));
      });
    };
    footerToggles.forEach(function (button) {
      button.addEventListener("click", function () {
        if (!footerMedia.matches) return;
        var column = button.closest(".footer__column");
        var open = column.classList.toggle("is-open");
        button.setAttribute("aria-expanded", String(open));
      });
    });
    footerMedia.addEventListener("change", syncFooter);
    syncFooter();
  }

  // Keep the collection navigator connected to the fragrance in view.
  var collectionIndex = document.querySelector("[data-collection-index]");
  if (collectionIndex && "IntersectionObserver" in window) {
    var collectionLinks = Array.from(collectionIndex.querySelectorAll("[data-collection-index-link]"));
    var collectionCards = document.querySelectorAll(".collection-grid--curated .perfume-card[id]");

    var setActiveFragrance = function (handle) {
      collectionLinks.forEach(function (link) {
        var active = link.getAttribute("data-collection-index-link") === handle;
        link.classList.toggle("is-active", active);
        if (active) link.setAttribute("aria-current", "true");
        else link.removeAttribute("aria-current");
      });
    };

    var collectionObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) setActiveFragrance(entry.target.id.replace("card-", ""));
        });
      },
      { threshold: 0.35, rootMargin: "-20% 0px -45% 0px" }
    );

    collectionCards.forEach(function (card) { collectionObserver.observe(card); });
    if (collectionLinks.length) setActiveFragrance(collectionLinks[0].getAttribute("data-collection-index-link"));
  }

  // Mobile hero fragrance slider. The cards remain native links and can still be swiped.
  var heroGallery = document.querySelector(".hero__gallery");
  var heroPanels = heroGallery ? Array.from(heroGallery.querySelectorAll(".hero__panel")) : [];
  var heroDots = Array.from(document.querySelectorAll("[data-hero-dot]"));
  if (heroGallery && heroPanels.length > 1 && heroDots.length && window.matchMedia("(max-width: 46rem)").matches) {
    var heroIndex = 0;
    var heroScrollTimer;
    var heroAutoplayTimer;
    var heroStatus = document.querySelector("[data-hero-status]");
    var updateHeroSlider = function (index) {
      heroIndex = Math.max(0, Math.min(index, heroPanels.length - 1));
      heroDots.forEach(function (dot, dotIndex) {
        var active = dotIndex === heroIndex;
        dot.classList.toggle("is-active", active);
        dot.setAttribute("aria-selected", String(active));
      });
      if (heroStatus) heroStatus.textContent = "Fragrance " + (heroIndex + 1) + " of " + heroPanels.length;
    };
    var goToHero = function (index) {
      updateHeroSlider(index);
      heroGallery.scrollTo({ left: heroPanels[heroIndex].offsetLeft, behavior: reduced ? "auto" : "smooth" });
    };
    heroDots.forEach(function (dot) {
      dot.addEventListener("click", function () { goToHero(Number(dot.getAttribute("data-hero-index"))); });
    });
    heroGallery.addEventListener("scroll", function () {
      window.clearTimeout(heroScrollTimer);
      heroScrollTimer = window.setTimeout(function () {
        var closest = heroPanels.reduce(function (best, panel, index) {
          return Math.abs(panel.offsetLeft - heroGallery.scrollLeft) < Math.abs(heroPanels[best].offsetLeft - heroGallery.scrollLeft) ? index : best;
        }, 0);
        updateHeroSlider(closest);
      }, 80);
    }, { passive: true });
    var stopHeroAutoplay = function () { window.clearInterval(heroAutoplayTimer); };
    var startHeroAutoplay = function () {
      if (reduced) return;
      stopHeroAutoplay();
      heroAutoplayTimer = window.setInterval(function () { goToHero((heroIndex + 1) % heroPanels.length); }, 5200);
    };
    heroGallery.addEventListener("mouseenter", stopHeroAutoplay);
    heroGallery.addEventListener("mouseleave", startHeroAutoplay);
    heroGallery.addEventListener("focusin", stopHeroAutoplay);
    heroGallery.addEventListener("focusout", startHeroAutoplay);
    updateHeroSlider(0);
    startHeroAutoplay();
  }

  document.addEventListener("keydown", function (event) {
    if (!mobileNav || !mobileNav.classList.contains("is-open")) return;

    if (event.key === "Escape") {
      event.preventDefault();
      setMobileNav(false);
      return;
    }

    if (event.key !== "Tab") return;
    var focusable = focusableElements(mobileNav);
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
})();
